/**
 * Линейный график на инлайновом SVG.
 *
 * Своими руками, без библиотеки: раздел 3 держит клиент лёгким (React + Vite,
 * ничего сверх), а любая графическая библиотека — это сотни килобайт на
 * связи в подвале зала ради двух ломаных линий.
 *
 * ВАЖНО (раздел 2, принцип 1): компонент НИЧЕГО не считает про тренировку.
 * e1RM, темп, прогноз приходят готовыми с сервера. Вся арифметика ниже —
 * это перевод значений в координаты пикселей, а не килограммы и не проценты.
 * Единственное, что делается с числом для показа, — форматирование подписи.
 */

import { useEffect, useRef, useState } from 'react'

export interface ChartPoint {
  /** ISO-дата (YYYY-MM-DD), как её отдаёт сервер. */
  date: string
  value: number
}

interface ChartProps {
  points: ChartPoint[]
  height?: number
  /**
   * Точка цели: от последнего факта до неё рисуется пунктир прогноза.
   * Передавать её можно ТОЛЬКО когда сервер сказал has_forecast = true
   * (раздел 5.5): пунктир — это утверждение о будущем, и на плохих данных
   * его быть не должно.
   */
  forecast?: { date: string; value: number }
  /** Горизонтальная линия целевого значения. */
  target?: number
}

/** Поля под подписи осей. Слева шире — там числа, снизу только даты. */
const PAD_LEFT = 38
const PAD_RIGHT = 14
const PAD_TOP = 16
const PAD_BOTTOM = 20

/** Ширина до первого замера: телефон в портрете за вычетом полей экрана. */
const FALLBACK_WIDTH = 340

function toTime(iso: string): number {
  // Полдень не нужен: сравниваются одни и те же календарные дни, а `T00:00:00`
  // без зоны трактуется как локальная полночь — одинаково для всех точек.
  return Date.parse(`${iso}T00:00:00`)
}

/** Число под график: без хвостовых нулей, не длиннее одного знака после точки. */
function short(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

/**
 * Дата для оси: «8 сент.». Полное «8 сентября» на оси не помещается.
 *
 * `withYear` нужен правому краю: дата прогноза до цели легко уезжает за Новый
 * год, и «14 мар.» рядом с «8 сент.» этого года читается как ближайшая весна —
 * подпись показывала бы срок ближе настоящего.
 */
function axisDate(iso: string, withYear = false): string {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  const base = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
  return withYear ? `${base} ${d.getFullYear()}` : base
}

/**
 * График «как есть»: точки сервера, линия между ними, пунктир прогноза.
 *
 * Ширина меряется у контейнера, а не берётся константой, и viewBox совпадает
 * с ней один в один. Так `preserveAspectRatio` ничего не растягивает: кружки
 * остаются кружками, а подписи — читаемыми на любой ширине экрана. Растянуть
 * viewBox по ширине (`preserveAspectRatio="none"`) было бы короче, но тогда
 * текст и точки размазывает по горизонтали тем сильнее, чем шире телефон.
 */
export default function Chart({ points, height = 180, forecast, target }: ChartProps) {
  const wrap = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(FALLBACK_WIDTH)

  useEffect(() => {
    const el = wrap.current
    if (!el) return
    const measure = () => setWidth(Math.max(220, Math.round(el.getBoundingClientRect().width)))
    measure()
    // Ширина меняется на повороте телефона и при открытии клавиатуры.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Ноль точек — рисовать нечего. Пустое состояние объясняет экран, а не график.
  if (points.length === 0) return null

  const left = PAD_LEFT
  const right = width - PAD_RIGHT
  const top = PAD_TOP
  const bottom = height - PAD_BOTTOM
  const innerW = Math.max(1, right - left)
  const innerH = Math.max(1, bottom - top)

  // --- вертикальная шкала -------------------------------------------------
  const values = points.map((p) => p.value)
  const dataMin = Math.min(...values)
  const dataMax = Math.max(...values)
  // Цель и прогноз тоже должны поместиться в кадр, иначе линия уедет за край.
  const extra: number[] = []
  if (forecast) extra.push(forecast.value)
  if (target !== undefined) extra.push(target)
  const lo = Math.min(dataMin, ...extra)
  const hi = Math.max(dataMax, ...extra)
  const vSpan = hi - lo
  const y = (v: number) => (vSpan === 0 ? top + innerH / 2 : bottom - ((v - lo) / vSpan) * innerH)

  // --- горизонтальная шкала ------------------------------------------------
  const times = points.map((p) => toTime(p.date))
  const datesOk = times.every((t) => !Number.isNaN(t))
  const forecastTime = forecast ? toTime(forecast.date) : Number.NaN
  const showForecast = forecast !== undefined && !Number.isNaN(forecastTime) && datesOk
  const tMin = datesOk ? Math.min(...times) : 0
  const tMaxData = datesOk ? Math.max(...times) : points.length - 1
  const tMax = showForecast ? Math.max(tMaxData, forecastTime) : tMaxData
  const tSpan = tMax - tMin
  // Одна точка (или все точки в один день) — tSpan равен нулю, и точка встаёт
  // по центру. Линию в этом случае рисовать не из чего и незачем.
  const x = (t: number) => (tSpan <= 0 ? left + innerW / 2 : left + ((t - tMin) / tSpan) * innerW)

  const plotted = points.map((p, i) => ({
    value: p.value,
    cx: x(datesOk ? (times[i] ?? tMin) : i),
    cy: y(p.value),
  }))

  const last = plotted.at(-1)
  const first = points.at(0)
  const lastPoint = points.at(-1)
  if (!last || !first || !lastPoint) return null

  const line =
    plotted.length > 1
      ? plotted.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.cx.toFixed(1)} ${p.cy.toFixed(1)}`).join(' ')
      : null

  // Подпись последней точки у самого края съезжает за кадр — прижимаем её.
  const labelAnchor = last.cx > right - 34 ? 'end' : 'middle'
  const labelY = last.cy - 12 < top ? last.cy + 18 : last.cy - 12
  // Крайняя правая дата — это дата прогноза, если он есть: именно она стоит
  // у правого края. Подписать её последним фактом значило бы соврать.
  const rightDate = showForecast && forecast ? forecast.date : lastPoint.date

  return (
    <div className="chart-wrap" ref={wrap}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`График: ${points.length} точек, от ${short(dataMin)} до ${short(dataMax)}`}
      >
        {/* Линия целевого значения */}
        {target !== undefined && (
          <line
            x1={left}
            x2={right}
            y1={y(target)}
            y2={y(target)}
            stroke="var(--text-faint)"
            strokeWidth="1"
            strokeDasharray="2 5"
          />
        )}

        {/* Ось времени */}
        <line x1={left} x2={right} y1={bottom + 4} y2={bottom + 4} stroke="var(--border)" strokeWidth="1" />

        {/* Пунктир прогноза: от последнего факта к точке цели */}
        {showForecast && forecast && (
          <>
            <line
              x1={last.cx}
              y1={last.cy}
              x2={x(forecastTime)}
              y2={y(forecast.value)}
              stroke="var(--accent)"
              strokeWidth="2"
              strokeDasharray="5 4"
              opacity="0.75"
            />
            <circle
              cx={x(forecastTime)}
              cy={y(forecast.value)}
              r="4.5"
              fill="var(--bg-raised)"
              stroke="var(--accent)"
              strokeWidth="2"
            />
          </>
        )}

        {/* Факт */}
        {line && (
          <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {plotted.map((p, i) => (
          <circle
            key={`${i}-${p.cx.toFixed(1)}`}
            cx={p.cx}
            cy={p.cy}
            r={i === plotted.length - 1 ? 5 : 2.5}
            fill={i === plotted.length - 1 ? 'var(--accent)' : 'var(--bg)'}
            stroke="var(--accent)"
            strokeWidth={i === plotted.length - 1 ? 0 : 1.5}
          />
        ))}

        {/* Подпись последней точки — то, где человек находится сейчас */}
        <text
          x={last.cx}
          y={labelY}
          textAnchor={labelAnchor}
          fontSize="13"
          fontWeight="600"
          fill="var(--text)"
        >
          {short(last.value)}
        </text>

        {/* Ось значений: только минимум и максимум факта.
            Нижнюю подпись прячем, когда она наезжает на верхнюю: кадр
            растянут до цели (жим 100 при текущих 62), и весь размах факта
            занимает несколько пикселей — два числа встали бы друг на друга. */}
        <text x={left - 8} y={y(dataMax) + 4} textAnchor="end" fontSize="11" fill="var(--text-faint)">
          {short(dataMax)}
        </text>
        {dataMin !== dataMax && y(dataMin) - y(dataMax) >= 13 && (
          <text x={left - 8} y={y(dataMin) + 4} textAnchor="end" fontSize="11" fill="var(--text-faint)">
            {short(dataMin)}
          </text>
        )}

        {/* Ось дат: первая и последняя */}
        <text x={left} y={height - 5} textAnchor="start" fontSize="11" fill="var(--text-faint)">
          {axisDate(first.date)}
        </text>
        {(points.length > 1 || showForecast) && (
          <text x={right} y={height - 5} textAnchor="end" fontSize="11" fill="var(--text-faint)">
            {axisDate(rightDate, rightDate.slice(0, 4) !== first.date.slice(0, 4))}
          </text>
        )}
      </svg>
    </div>
  )
}
